import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { AdminNav } from "../../components/admin/AdminNav";
import { formatDate } from "../../lib/format";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Plus, Pencil, Copy, Trash2, ExternalLink, Loader2 } from "lucide-react";

export default function AdminCoursesPage() {
  const [courses, setCourses] = useState(null);

  const load = () => api.get("/admin/courses").then((r) => setCourses(r.data)).catch(() => setCourses([]));

  useEffect(() => { load(); }, []);

  const duplicate = async (id) => {
    try {
      await api.post(`/admin/courses/${id}/duplicate`);
      toast.success("Corso duplicato");
      load();
    } catch { toast.error("Errore durante la duplicazione"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/courses/${id}`);
      toast.success("Corso eliminato");
      load();
    } catch { toast.error("Errore durante l'eliminazione"); }
  };

  return (
    <div className="min-h-screen bg-[#F4F8FF]">
      <AdminNav />
      <div className="px-6 md:px-10 max-w-[1400px] mx-auto py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-light text-[#0B1220]">Corsi</h1>
            <p className="text-[#5C6678] mt-1">Crea, modifica e gestisci le landing dei corsi.</p>
          </div>
          <Link
            to="/admin/corsi/nuovo"
            data-testid="new-course-btn"
            className="inline-flex items-center gap-2 bg-[#1452F0] text-[#F4F8FF] hover:bg-[#0E3DC0] transition-colors rounded-sm px-6 py-3 text-sm font-medium"
          >
            <Plus className="h-4 w-4" /> Nuovo corso
          </Link>
        </div>

        {courses === null ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1452F0]" /></div>
        ) : courses.length === 0 ? (
          <div className="py-20 text-center text-[#5C6678]">Nessun corso. Crea il primo corso.</div>
        ) : (
          <div className="bg-white rounded-sm border border-[#D5DEEE] overflow-hidden">
            <table className="w-full text-sm" data-testid="courses-table">
              <thead>
                <tr className="border-b border-[#D5DEEE] text-left text-[#5C6678] text-xs uppercase tracking-wider">
                  <th className="px-5 py-4 font-medium">Corso</th>
                  <th className="px-5 py-4 font-medium hidden md:table-cell">Data</th>
                  <th className="px-5 py-4 font-medium hidden md:table-cell">Città</th>
                  <th className="px-5 py-4 font-medium">Stato</th>
                  <th className="px-5 py-4 font-medium text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((c) => (
                  <tr key={c.id} className="border-b border-[#D5DEEE] last:border-0 hover:bg-[#E9EFFA]/50" data-testid={`course-row-${c.id}`}>
                    <td className="px-5 py-4">
                      <div className="font-medium text-[#0B1220]">{c.title}</div>
                      <div className="text-xs text-[#5C6678]">{c.category} · {c.speaker_name}</div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-[#5C6678]">{formatDate(c.date_time)}</td>
                    <td className="px-5 py-4 hidden md:table-cell text-[#5C6678]">{c.city}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${c.status === "active" ? "bg-[#1452F0]/15 text-[#1452F0]" : "bg-[#5C6678]/15 text-[#5C6678]"}`}>
                        {c.status === "active" ? "Attivo" : "Disattivo"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <a href={`/corsi/${c.slug}`} target="_blank" rel="noopener noreferrer" title="Anteprima" className="p-2 rounded-sm hover:bg-[#D5DEEE] text-[#5C6678]" data-testid={`preview-${c.id}`}>
                          <ExternalLink className="h-4 w-4" />
                        </a>
                        <Link to={`/admin/corsi/${c.id}`} title="Modifica" className="p-2 rounded-sm hover:bg-[#D5DEEE] text-[#1452F0]" data-testid={`edit-${c.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                        <button onClick={() => duplicate(c.id)} title="Duplica" className="p-2 rounded-sm hover:bg-[#D5DEEE] text-[#5C6678]" data-testid={`duplicate-${c.id}`}>
                          <Copy className="h-4 w-4" />
                        </button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button title="Elimina" className="p-2 rounded-sm hover:bg-[#A63C3C]/10 text-[#A63C3C]" data-testid={`delete-${c.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#F4F8FF]">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-heading">Eliminare il corso?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Questa azione non può essere annullata. Il corso "{c.title}" verrà eliminato definitivamente.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annulla</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(c.id)} className="bg-[#A63C3C] hover:bg-[#8a3030]" data-testid={`confirm-delete-${c.id}`}>
                                Elimina
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
